import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Brain as BrainIcon, ArrowUpRight, ArrowDownRight, ShieldCheck, ShieldOff,
  Clock1, Clock3, Layers, Gauge, AlertTriangle, CheckCircle2, XCircle, MinusCircle, Clock, Eye,
} from "lucide-react";
import api from "@/lib/api";
import LevelChart from "@/components/LevelChart";
import StrategyFiles from "./StrategyFiles";

const NY_TIME = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit", second: "2-digit" });
const nyTime = (iso) => {
  if (!iso) return null;
  const s = String(iso).replace(" ", "T").replace(/(\.\d{3})\d+/, "$1");
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : s + "Z");   // naive times are UTC
  return isNaN(d) ? null : NY_TIME.format(d);
};
const fmtLevel = (v) => (typeof v === "number" ? v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : null);

const useIsPhone = () => {
  const [isPhone, setIsPhone] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isPhone;
};

const CHECK_STYLE = {
  ok: { Icon: CheckCircle2, cls: "text-[hsl(var(--ops-long))]", sr: "done" },
  blocked: { Icon: XCircle, cls: "text-[hsl(var(--ops-short))]", sr: "not yet" },
  waiting: { Icon: Clock, cls: "text-[hsl(var(--ops-warn))]", sr: "waiting" },
  not_checked: { Icon: MinusCircle, cls: "text-[hsl(var(--ops-text-muted))]", sr: "not checked" },
};

function Checklist({ items, testId }) {
  if (!items?.length) return null;
  return (
    <ul data-testid={testId} className="space-y-2">
      {items.map((it, i) => {
        const s = CHECK_STYLE[it.status] || CHECK_STYLE.not_checked;
        return (
          <li key={`${it.key}-${i}`} className="flex items-start gap-2 min-w-0" title={it.raw || undefined}>
            <s.Icon className={`size-4 mt-0.5 shrink-0 ${s.cls}`} aria-label={s.sr} />
            <div className="min-w-0 break-words">
              <div className="text-[13px] font-medium text-[hsl(var(--ops-text))] font-mono-num">{it.label}</div>
              {it.why && <div className="text-xs text-[hsl(var(--ops-text-muted))] leading-relaxed font-mono-num">{it.why}</div>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function TrendChip({ chip }) {
  const up = chip.trend === "up" || chip.trend === "strong_up";
  const down = chip.trend === "down" || chip.trend === "strong_down";
  const strong = chip.trend?.startsWith("strong_");
  const cls = up
    ? `${strong ? "bg-[hsl(var(--ops-long))]/25" : "bg-[hsl(var(--ops-long))]/10"} text-[hsl(var(--ops-long))]`
    : down
      ? `${strong ? "bg-[hsl(var(--ops-short))]/25" : "bg-[hsl(var(--ops-short))]/10"} text-[hsl(var(--ops-short))]`
      : "bg-[hsl(var(--ops-text-muted))]/15 text-[hsl(var(--ops-text-muted))]";
  return (
    <Badge className={`rounded text-[10px] border-0 px-1.5 gap-0.5 ${strong ? "font-semibold" : "font-medium"} ${cls}`}
      title={`${chip.label} trend: ${chip.text}`}>
      {up ? <ArrowUpRight className="size-3" /> : down ? <ArrowDownRight className="size-3" /> : <MinusCircle className="size-3" />}
      {chip.label} {chip.text}
    </Badge>
  );
}

function WhatBotSees({ detail, error }) {
  const isPhone = useIsPhone();
  const [tf, setTf] = useState("3m");
  const card = "p-4 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]";
  const title = (
    <div className="flex items-center gap-2 mb-2">
      <Eye className="size-4 text-[hsl(var(--ops-brand))]" />
      <span className="text-sm font-semibold text-[hsl(var(--ops-text))]">What the bot is looking at</span>
    </div>
  );
  if (!detail) {
    return (
      <Card data-testid="ops-brain-explain" className={card}>
        {title}
        {error
          ? <p className="text-xs text-[hsl(var(--ops-text-muted))]">Could not load the bot's details right now. Retrying.</p>
          : <Skeleton className="h-40 bg-[hsl(var(--ops-surface-2))]" />}
      </Card>
    );
  }
  const ex = detail.explanation;
  const setup = detail.setup;
  const tstop = detail.technical_stop;
  const sameCheck = tstop && setup && tstop.updated_at === setup.updated_at;
  const stale = setup?.stale;
  const lines = [];
  const stopPrice = sameCheck && typeof tstop.stop_price === "number" ? tstop.stop_price : setup?.stop_reference;
  const hasLevel = setup && typeof setup.level === "number";
  // stop at the level (within half a tick): one line, not two overlapping labels
  const stopIsLevel = hasLevel && typeof stopPrice === "number" && Math.abs(stopPrice - setup.level) < 0.125;
  if (hasLevel) {
    const name = stopIsLevel ? `Level / stop ${fmtLevel(setup.level)}` : setup.level_label || "Level";
    lines.push({ price: setup.level, tone: stopIsLevel ? "short" : "brand", dashed: !!stale,
      title: stale ? `Last setup${stopIsLevel ? " level / stop" : ""} (stale)` : name });
  }
  if (setup && typeof stopPrice === "number" && !stopIsLevel) lines.push({ price: stopPrice, tone: "short", dashed: true, title: stale ? "Stop (stale)" : "Stop" });
  const targetPrice = sameCheck && typeof tstop.target_price === "number" ? tstop.target_price : setup?.target_reference;
  if (setup && typeof targetPrice === "number") lines.push({ price: targetPrice, tone: "long", dashed: true, title: stale ? "Target (stale)" : "Target" });
  const candles = detail.candles?.[tf] || [];
  const chips = detail.trends?.chips || [];
  const last = ex?.last_setup;

  return (
    <Card data-testid="ops-brain-explain" className={card}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        {title}
        {detail.brain?.updated_at && (
          <span className="text-[10px] text-[hsl(var(--ops-text-muted))] font-mono-num">Score updated {nyTime(detail.brain.updated_at)} New York time</span>
        )}
      </div>
      {ex?.headline && (
        <p data-testid="ops-brain-headline" className="text-sm leading-relaxed text-[hsl(var(--ops-text))] font-mono-num break-words mb-4">{ex.headline}</p>
      )}

      <div className="grid gap-4 lg:grid-cols-5 min-w-0">
        <div className="lg:col-span-2 space-y-4 min-w-0">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))] mb-2">
              {ex?.waiting_for?.some((i) => i.status !== "ok") ? "Waiting for" : "Checks"}
            </div>
            {ex?.waiting_for?.length
              ? <Checklist items={ex.waiting_for} testId="ops-brain-waiting" />
              : <p className="text-xs text-[hsl(var(--ops-text-muted))]">The bot has not reported its checks yet.</p>}
          </div>

          {last && (
            <div data-testid="ops-brain-last-setup" className="rounded-md border border-[hsl(var(--ops-border))] bg-[hsl(var(--ops-surface-2))]/40 p-3">
              <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))] mb-1">
                Last setup checked at {last.checked_at_ny || "unknown time"}{last.age_text ? ` (${last.age_text})` : ""}{last.stale ? ", stale" : ""}
              </div>
              <p className="text-xs text-[hsl(var(--ops-text))] leading-relaxed font-mono-num break-words mb-2">{last.sentence}</p>
              <Checklist items={last.checks} testId="ops-brain-last-checks" />
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))] mb-2">
              Trends{detail.trends?.checked_at_ny ? ` (as of ${detail.trends.checked_at_ny})` : ""}
            </div>
            {chips.length
              ? <div data-testid="ops-brain-trends" className="flex flex-wrap gap-1.5">{chips.map((c) => <TrendChip key={c.key} chip={c} />)}</div>
              : <p className="text-xs text-[hsl(var(--ops-text-muted))]">No trend readings from the bot yet.</p>}
          </div>

          {detail.mirror?.note && (
            <p className="text-[11px] text-[hsl(var(--ops-warn))] leading-relaxed">{detail.mirror.note}</p>
          )}
        </div>

        <div className="lg:col-span-3 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))]">MNQ, live</div>
            <div role="group" aria-label="Candle size" className="flex rounded-md border border-[hsl(var(--ops-border))] overflow-hidden">
              {["1m", "3m"].map((v) => (
                <button key={v} type="button" data-testid={`brain-chart-tf-${v}`} onClick={() => setTf(v)}
                  className={`px-2.5 py-1 text-[11px] font-medium ${tf === v ? "bg-[hsl(var(--ops-brand))]/15 text-[hsl(var(--ops-brand))]" : "text-[hsl(var(--ops-text-muted))] hover:text-[hsl(var(--ops-text))]"}`}>
                  {v === "1m" ? "1-minute" : "3-minute"}
                </button>
              ))}
            </div>
          </div>
          {candles.length
            ? <LevelChart candles={candles} lines={lines} height={isPhone ? 220 : 260} fitKey={tf} />
            : <div className="flex items-center justify-center text-xs text-[hsl(var(--ops-text-muted))] rounded-md border border-dashed border-[hsl(var(--ops-border))]"
                style={{ height: isPhone ? 220 : 260 }}>No price bars stored yet.</div>}
          <p className="mt-1.5 text-[11px] text-[hsl(var(--ops-text-muted))] leading-relaxed font-mono-num break-words">
            {tf === "1m" ? "1-minute" : "3-minute"} candles, last 2 hours, times in New York time.
            {setup
              ? ` ${stale ? "Last setup (stale)" : stopIsLevel ? "Level / stop" : setup.level_label || "Level"}${fmtLevel(setup.level) ? ` ${fmtLevel(setup.level)}` : ""}`
                + `${typeof stopPrice === "number" && !stopIsLevel ? `, stop ${fmtLevel(stopPrice)}` : ""}`
                + `${stale && stopIsLevel ? " (level and stop)" : ""}`
                + `${typeof targetPrice === "number" ? `, target ${fmtLevel(targetPrice)}` : ""}`
                + `, last checked ${setup.checked_at_ny || "at an unknown time"}.`
              : " No setup from the bot yet, so no level is drawn."}
          </p>
        </div>
      </div>
    </Card>
  );
}

function SignalCard({ testId, label, icon: Icon, signal }) {
  if (!signal) return null;
  const statusColor = {
    confirmed: 'ops-long',
    pending: 'ops-warn',
    rejected: 'ops-short',
    neutral: 'text-[hsl(var(--ops-text-muted))]',
    conflicting: 'ops-warn',
  };
  const StatusIcon = {
    confirmed: CheckCircle2,
    pending: MinusCircle,
    rejected: XCircle,
    neutral: MinusCircle,
    conflicting: AlertTriangle,
  };
  const SIcon = StatusIcon[signal.status] || MinusCircle;
  return (
    <Card data-testid={testId} className="p-3 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="size-4 text-[hsl(var(--ops-brand))]" />
          <span className="text-xs font-semibold text-[hsl(var(--ops-text))]">{label}</span>
        </div>
        <Badge className={`rounded text-[10px] border-0 px-1.5 gap-1 ${signal.status === 'confirmed' ? 'bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]' : signal.status === 'rejected' ? 'bg-[hsl(var(--ops-short))]/15 text-[hsl(var(--ops-short))]' : signal.status === 'pending' || signal.status === 'conflicting' ? 'bg-[hsl(var(--ops-warn))]/15 text-[hsl(var(--ops-warn))]' : 'bg-[hsl(var(--ops-text-muted))]/15 text-[hsl(var(--ops-text-muted))]'}`}>
          <SIcon className="size-3" />
          {signal.status?.toUpperCase()}
        </Badge>
      </div>
      <div className="flex items-center gap-3 mb-2">
        <Badge className={`rounded text-[10px] border-0 px-1.5 ${signal.direction === 'LONG' ? 'bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]' : 'bg-[hsl(var(--ops-short))]/15 text-[hsl(var(--ops-short))]'}`}>
          {signal.direction === 'LONG' ? <ArrowUpRight className="size-3 mr-0.5" /> : <ArrowDownRight className="size-3 mr-0.5" />}
          {signal.direction}
        </Badge>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-[10px] text-[hsl(var(--ops-text-muted))]">Strength</span>
            <span className="text-[10px] font-mono-num text-[hsl(var(--ops-text))]">{(signal.strength * 100).toFixed(0)}%</span>
          </div>
          <Progress value={signal.strength * 100} className="h-1.5 bg-[hsl(var(--ops-surface-2))]" />
        </div>
      </div>
      <p className="text-[11px] text-[hsl(var(--ops-text-muted))]">{signal.detail}</p>
    </Card>
  );
}

export default function BrainPage() {
  const { autoRefresh } = useOutletContext();
  const [brain, setBrain] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/ops/brain");
        setBrain(data);
      } catch (e) { /* silent */ }
      setLoading(false);
    };
    load();
    if (!autoRefresh) return;
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, [autoRefresh]);

  // what the bot is looking at: setup, checks, trends and ~2 h of bars (polled every 5 s)
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState(false);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/ops/brain/detail");
        if (alive) { setDetail(data); setDetailError(false); }
      } catch (e) { if (alive) setDetailError(true); }
    };
    load();
    if (!autoRefresh) return () => { alive = false; };
    const iv = setInterval(load, 5000);
    return () => { alive = false; clearInterval(iv); };
  }, [autoRefresh]);

  if (loading) return (
    <div className="space-y-4"><Skeleton className="h-8 w-48 bg-[hsl(var(--ops-surface))]" /><div className="grid gap-3 lg:grid-cols-2">{Array.from({length:4}).map((_,i)=><Skeleton key={i} className="h-32 bg-[hsl(var(--ops-surface))]" />)}</div></div>
  );

  if (!brain || brain.message) return (
    <div className="flex items-center justify-center h-64 text-[hsl(var(--ops-text-muted))] text-sm">No brain data available</div>
  );

  const gateAllow = brain.trade_gate?.status === 'ALLOW';
  const scoreColor = brain.mentor_score >= 70 ? 'hsl(var(--ops-long))' : brain.mentor_score >= 50 ? 'hsl(var(--ops-warn))' : 'hsl(var(--ops-short))';

  return (
    <div data-testid="ops-brain-page" className="space-y-5">
      <div>
        <h1 className="text-lg font-display font-semibold text-[hsl(var(--ops-text))]">MentorAI Brain</h1>
        <p className="text-xs text-[hsl(var(--ops-text-muted))]">What the trading engine is currently thinking</p>
      </div>

      <WhatBotSees detail={detail} error={detailError} />

      <StrategyFiles />

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Mentor Score */}
        <Card data-testid="ops-mentor-score-gauge" className="p-4 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] flex flex-col items-center justify-center">
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))] mb-3">Mentor Score</div>
          <div className="relative size-32 flex items-center justify-center">
            <svg className="size-32 -rotate-90" viewBox="0 0 36 36">
              <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="hsl(var(--ops-surface-2))" strokeWidth="3" />
              <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke={scoreColor} strokeWidth="3" strokeDasharray={`${brain.mentor_score}, 100`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-mono-num font-bold" style={{ color: scoreColor }}>{brain.mentor_score}</span>
              <span className="text-[10px] text-[hsl(var(--ops-text-muted))]">/ 100</span>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Badge className={`rounded text-[11px] border-0 px-2 py-0.5 gap-1 ${brain.direction === 'LONG' ? 'bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]' : 'bg-[hsl(var(--ops-short))]/15 text-[hsl(var(--ops-short))]'}`}>
              {brain.direction === 'LONG' ? <ArrowUpRight className="size-3.5" /> : <ArrowDownRight className="size-3.5" />}
              {brain.direction}
            </Badge>
          </div>
          {brain.detected_setup && (
            <div className="mt-2 text-xs text-[hsl(var(--ops-text-muted))]">
              Setup: <span className="text-[hsl(var(--ops-text))] font-medium">{brain.detected_setup}</span>
            </div>
          )}
        </Card>

        {/* Trade Gate */}
        <Card data-testid="ops-trade-gate-status" className="p-4 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
          <div className="flex items-center gap-2 mb-3">
            {gateAllow ? <ShieldCheck className="size-5 text-[hsl(var(--ops-long))]" /> : <ShieldOff className="size-5 text-[hsl(var(--ops-short))]" />}
            <span className="text-sm font-semibold text-[hsl(var(--ops-text))]">Trade Gate</span>
          </div>
          <Badge className={`rounded text-sm border-0 px-3 py-1 font-semibold ${gateAllow ? 'bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]' : 'bg-[hsl(var(--ops-short))]/15 text-[hsl(var(--ops-short))]'}`}>
            {brain.trade_gate?.status}
          </Badge>
          <p className="mt-3 text-xs text-[hsl(var(--ops-text-muted))] leading-relaxed">{brain.trade_gate?.reason}</p>

          <Separator className="my-3 bg-[hsl(var(--ops-border))]" />

          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))] mb-2">Risk Assessment</div>
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-[hsl(var(--ops-text-muted))]">Daily Loss Used</span>
              <span className="font-mono-num text-[hsl(var(--ops-text))]">{brain.risk_assessment?.daily_loss_used_pct}%</span>
            </div>
            <Progress value={brain.risk_assessment?.daily_loss_used_pct || 0} className="h-1.5 bg-[hsl(var(--ops-surface-2))]" />
            <div className="flex justify-between text-xs">
              <span className="text-[hsl(var(--ops-text-muted))]">Positions</span>
              <span className="font-mono-num text-[hsl(var(--ops-text))]">{brain.risk_assessment?.current_positions} / {brain.risk_assessment?.max_positions}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-[hsl(var(--ops-text-muted))]">Portfolio Heat</span>
              <span className="font-mono-num text-[hsl(var(--ops-text))]">{brain.risk_assessment?.portfolio_heat_pct}%</span>
            </div>
          </div>
        </Card>

        {/* Signal Stack */}
        <div className="space-y-3">
          <SignalCard testId="ops-signal-1m" label="1-Minute Confirmation" icon={Clock1} signal={brain.signal_1m} />
          <SignalCard testId="ops-signal-3m" label="3-Minute Primary" icon={Clock3} signal={brain.signal_3m} />
          <SignalCard testId="ops-signal-htf" label="Higher Timeframe" icon={Layers} signal={brain.signal_htf} />
        </div>
      </div>
    </div>
  );
}
